// Comprehensive lookup table for Gambian locations with coordinates
export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  name: string;
}

export const GAMBIAN_LOCATIONS: Record<string, LocationCoordinates> = {
  // Greater Banjul Area
  'banjul': { latitude: 13.4549, longitude: -16.5790, name: 'Banjul' },
  'serrekunda': { latitude: 13.4348, longitude: -16.6773, name: 'Serrekunda' },
  'kanifing': { latitude: 13.4263, longitude: -16.6710, name: 'Kanifing' },
  'bakoteh': { latitude: 13.4289, longitude: -16.6234, name: 'Bakoteh' },
  'pipeline': { latitude: 13.4543, longitude: -16.6345, name: 'Pipeline' },
  'westfield': { latitude: 13.4412, longitude: -16.6789, name: 'Westfield' },
  'latrikunda': { latitude: 13.4298, longitude: -16.6456, name: 'Latrikunda' },
  'dippa kunda': { latitude: 13.4234, longitude: -16.6567, name: 'Dippa Kunda' },
  'tallinding': { latitude: 13.4456, longitude: -16.6234, name: 'Tallinding' },
  'sukuta': { latitude: 13.4123, longitude: -16.6789, name: 'Sukuta' },
  
  // Kololi/Coastal Area
  'kololi': { latitude: 13.4567, longitude: -16.7234, name: 'Kololi' },
  'senegambia': { latitude: 13.4534, longitude: -16.7123, name: 'Senegambia' },
  'kotu': { latitude: 13.4623, longitude: -16.7345, name: 'Kotu' },
  'bijilo': { latitude: 13.4678, longitude: -16.7456, name: 'Bijilo' },
  'cape point': { latitude: 13.4734, longitude: -16.7567, name: 'Cape Point' },
  'brufut': { latitude: 13.4123, longitude: -16.7678, name: 'Brufut' },
  'tanji': { latitude: 13.3567, longitude: -16.7789, name: 'Tanji' },
  
  // Brusubi Area
  'brusubi': { latitude: 13.4012, longitude: -16.6890, name: 'Brusubi' },
  'brusubi phase 1': { latitude: 13.4023, longitude: -16.6878, name: 'Brusubi Phase 1' },
  'brusubi phase 2': { latitude: 13.4001, longitude: -16.6901, name: 'Brusubi Phase 2' },
  'brusubi phase 3': { latitude: 13.3989, longitude: -16.6912, name: 'Brusubi Phase 3' },
  'brusubi phase 4': { latitude: 13.3978, longitude: -16.6923, name: 'Brusubi Phase 4' },
  
  // Coastal Highway
  'gunjur': { latitude: 13.2012, longitude: -16.7345, name: 'Gunjur' },
  'kartong': { latitude: 13.0567, longitude: -16.7789, name: 'Kartong' },
  'sanyang': { latitude: 13.1234, longitude: -16.7567, name: 'Sanyang' },
  'tujering': { latitude: 13.1789, longitude: -16.7234, name: 'Tujering' },
  
  // Combo Area
  'combo': { latitude: 13.3789, longitude: -16.6234, name: 'Combo' },
  'gunjur village': { latitude: 13.3567, longitude: -16.6123, name: 'Gunjur Village' },
  'after mile 7': { latitude: 13.3456, longitude: -16.6012, name: 'After Mile 7' },
  'mile 7': { latitude: 13.3678, longitude: -16.6345, name: 'Mile 7' },
  
  // Other Major Areas
  'bundung': { latitude: 13.4234, longitude: -16.6012, name: 'Bundung' },
  'jambanjelly': { latitude: 13.3789, longitude: -16.5890, name: 'Jambanjelly' },
  'soma': { latitude: 13.4345, longitude: -15.5234, name: 'Soma' },
  'brikama': { latitude: 13.2704, longitude: -16.6490, name: 'Brikama' },
  'farafenni': { latitude: 13.5667, longitude: -15.6000, name: 'Farafenni' },
  'basse': { latitude: 13.3167, longitude: -14.2167, name: 'Basse' },
  'georgetown': { latitude: 13.5333, longitude: -14.7667, name: 'Georgetown' },
  'janjanbureh': { latitude: 13.5333, longitude: -14.7667, name: 'Janjanbureh' },
  
  // Airport Area
  'yundum': { latitude: 13.3567, longitude: -16.6789, name: 'Yundum' },
  'airport': { latitude: 13.3378, longitude: -16.6522, name: 'Banjul International Airport' },
  
  // Additional neighborhoods
  'fajara': { latitude: 13.4789, longitude: -16.6890, name: 'Fajara' },
  'bakau': { latitude: 13.4789, longitude: -16.6789, name: 'Bakau' },
  'cape st mary': { latitude: 13.4890, longitude: -16.6678, name: 'Cape St Mary' },
  'new town': { latitude: 13.4567, longitude: -16.6456, name: 'New Town' },
  'half die': { latitude: 13.4345, longitude: -16.6234, name: 'Half Die' },
  'tobacco road': { latitude: 13.4234, longitude: -16.6123, name: 'Tobacco Road' },
  'churchill town': { latitude: 13.4123, longitude: -16.6012, name: 'Churchill Town' },
  'manjai': { latitude: 13.4012, longitude: -16.5901, name: 'Manjai' },
  'wellingara': { latitude: 13.3901, longitude: -16.5790, name: 'Wellingara' },
};

// Function to find the best matching location for a given address
export function findLocationCoordinates(address: string): LocationCoordinates | null {
  if (!address) return null;
  
  const normalizedAddress = address.toLowerCase().trim();
  
  // First try exact match
  if (GAMBIAN_LOCATIONS[normalizedAddress]) {
    return GAMBIAN_LOCATIONS[normalizedAddress];
  }
  
  // Try partial matches - find the location name that appears in the address
  for (const [locationKey, coords] of Object.entries(GAMBIAN_LOCATIONS)) {
    if (normalizedAddress.includes(locationKey) || locationKey.includes(normalizedAddress)) {
      return coords;
    }
  }
  
  // Try fuzzy matching for common variations
  const fuzzyMatches: Record<string, string> = {
    'brusubi p1': 'brusubi phase 1',
    'brusubi p2': 'brusubi phase 2',
    'brusubi p3': 'brusubi phase 3',
    'brusubi p4': 'brusubi phase 4',
    'serrekunda central': 'serrekunda',
    'sk': 'serrekunda',
    'senegambia strip': 'senegambia',
    'kololi beach': 'kololi',
    'kotu stream': 'kotu',
    'bijilo forest': 'bijilo',
    'cape point beach': 'cape point',
    'airport road': 'yundum',
    'banjul airport': 'airport',
    'combo sillah': 'combo',
    'mile seven': 'mile 7',
    'after mile seven': 'after mile 7',
  };
  
  for (const [variation, canonical] of Object.entries(fuzzyMatches)) {
    if (normalizedAddress.includes(variation)) {
      return GAMBIAN_LOCATIONS[canonical] || null;
    }
  }
  
  // If no match found, return approximate coordinates for Greater Banjul Area
  return {
    latitude: 13.4348,
    longitude: -16.6773,
    name: 'Greater Banjul Area (Estimated)'
  };
}

// Function to get distance between two coordinates using existing calculation
export function getDistanceFromAddress(
  userLat: number,
  userLng: number,
  restaurantAddress: string
): { distance: number; coordinates: LocationCoordinates | null } {
  const coords = findLocationCoordinates(restaurantAddress);
  if (!coords) {
    return { distance: 0, coordinates: null };
  }
  
  // Use Haversine formula (same as existing calculateDistance function)
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRadians(coords.latitude - userLat);
  const dLng = toRadians(coords.longitude - userLng);
  
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(userLat)) *
    Math.cos(toRadians(coords.latitude)) *
    Math.sin(dLng / 2) *
    Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return {
    distance: Math.round(distance * 100) / 100, // Round to 2 decimal places
    coordinates: coords
  };
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}
